/// <reference types="dat.gui" />
import { createFileRoute } from "@tanstack/react-router";
import { defaultGroundProp } from "building-generation/elements/site/propSite";
import { DRACO_ASSET_SCALE, GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import { LayerConfigIdToJson } from "common/materials/library/layerConfigIdToJson";
import { TemplateIdToName } from "common/materials/library/templateIdToName";
import { MasonryTrimKind } from "common/materials/masonryTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialSrcFromPathString } from "common/materials/materialSrcFromPathString";
import { assertIsNotNull, assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import diffObjects from "common/utils/diffObjects";
import downloadFile from "common/utils/downloadFile";
import { enumKeys } from "common/utils/enum";
import { applyPatch } from "fast-json-patch";
import { aabb2New } from "geom/aabb2Types";
import { faceNew } from "geom/brep";
import { fillHatchLinesOnCanvas } from "geom/hatch";
import { HatchMaterialId } from "geom/hatchMaterialId";
import { planeNewXY } from "geom/plane";
import { polyLine2MakeAxisAlignedRect } from "geom/polyLine2";
import { UVGeneration } from "geom/uv";
import type { CameraSettings } from "gfx/CameraSettings";
import type { CanvasControls } from "gfx/CanvasControls";
import * as convert from "gfx/convert";
import { EnvPreset } from "gfx/EnvPreset";
import { hatchMaterialFromDesignMaterial } from "gfx/hatchMaterialFromDesignMaterial";
import { HTTPMaterialParameterLoader } from "gfx/HTTPMaterialParameterLoader";
import { SITE_LAYER } from "gfx/layerConstants";
import { assignToBuildingExteriorLayer, assignToSiteLayer } from "gfx/layers";
import type { HeightMap } from "gfx/materials/baking/heightMapFromDesignMaterial";
import {
  forgetCachedHeightmaps,
  heightMapCacheCount,
  heightMapFromDesignMaterial,
  renderHeightMapRenderer,
} from "gfx/materials/baking/heightMapFromDesignMaterial";
import { designMaterialNew, setDesignMaterialOptions } from "gfx/materials/designMaterial";
import {
  PeriodType,
  SdfPeriodPurpose,
  applyLayerConfig,
  designMaterialParamsEqual,
  extractLayerConfig,
  formatMaterialJSON,
  normalizeSdfPatternRotation,
  parseDesignMaterialParams,
  parseLayerConfig,
  preprocessMaterialParams,
  sdfCompileTimeSettingsMatch,
  sdfPatternsListTrim,
  sdfPatternsPopulateToMax,
  sdfPeriod,
} from "gfx/materials/designMaterialParams";
import { designMaterialRenderImageOptimized } from "gfx/materials/designMaterialRenderImage";
import type { DesignMaterialLoadOptions } from "gfx/materials/designMaterialRuntimeConfig";
import { BakeColorTextureMode } from "gfx/materials/designMaterialRuntimeConfig";
import {
  DEFAULT_DETAIL_RENDER_MODE,
  DetailRenderMode,
  ComponentViewMode,
  defaultDesignMaterialLoadOptions,
  LayerViewMode,
} from "gfx/materials/designMaterialRuntimeConfig";
import { generateMaterialTrimVariant } from "gfx/materials/designMaterialTrim";
import type { ColorLibraryParams, LayerConfigLibraryParams } from "gfx/materials/designMaterialTypes";
import type { DesignMaterialParams, LayerConfigParams } from "gfx/materials/designMaterialTypes";
import { cloneDefaultMaterialParams } from "gfx/materials/designMaterialUtils";
import { generateThreeGeometryFromDesignMaterial } from "gfx/materials/discretization/generateThreeGeometryFromDesignMaterial";
import { generateThreeGeometryWithinFaces } from "gfx/materials/discretization/generateThreeGeometryWithinFaces";
import { getLayoutInfoForMaterial, uvLayoutInfoNew } from "gfx/materials/discretization/utils";
import { initMaterialEditorGUI, layersUIColorLight } from "gfx/materials/materialEditorGUI";
import { loadAllMaterialEditorTextures } from "gfx/materials/materialEditorTextures";
import { ChannelVisualizationFragmentShader } from "gfx/materials/shaders/ChannelVisualizationFragmentShader";
import OrthographicControls from "gfx/OrthographicControls";
import PerspectiveControls from "gfx/PerspectiveControls";
import type { SceneLightSettings } from "gfx/sceneLights";
import { envPresetSceneLights } from "gfx/sceneLights";
import THREEAssetLoader from "gfx/THREEAssetLoader";
import { TransformControls } from "gfx/threejs/examples/jsm/controls/TransformControls";
import { DoubleSidedPlaneGeometry } from "gfx/threejs/higharc/DoubleSidedPlaneGeometry";
import type { SHLightProbeGrid } from "gfx/threejs/higharc/SHLightProbeGrid";
import { SSFXRendererOutput } from "gfx/threejs/higharc/SSFXRendererOutput";
import { UVVertexShader } from "gfx/threejs/higharc/UVVertexShader";
import THREEViewer from "gfx/THREEViewer";
import type { THREEViewerRenderer } from "gfx/THREEViewerRenderer";
import { GLMemoryHistoryGraph } from "lib/components/debug/GLMemoryHistoryGraph";
import LibraryList, { LibraryListAddButton } from "lib/components/material_editor/LibraryList";
import styles from "lib/components/material_editor/materialEditor.module.scss";
import { getMaterialPathAutofillOptions } from "lib/components/studio/inspector/paletteMaterialUtils";
import { defaultHatchMaterialFactory } from "lib/house/plans/hatchMaterialFactory";
import getPageTitle from "lib/utils/getPageTitle";
import React from "react";
import { Helmet as Head } from "react-helmet-async";
import type { Texture } from "three";
import {
  BoxGeometry,
  CameraHelper,
  Color,
  LineSegments,
  Mesh,
  MeshPhysicalMaterial,
  Object3D,
  OrthographicCamera,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
  WireframeGeometry,
} from "three";
import type * as THREE from "three";
import Select from "ui/forms/Select";
import withAuthentication from "../../components/withAuthentication";

const BOX_GEOMETRY_SIZE = 120;
const SPHERE_GEOMETRY_RADIUS = 80;
const LARGE_PLANE_GEOMETRY_SIZE = 600;

const PARAMS_LOCAL_STORAGE_KEY = "_higharcDesignMaterial";
const TEMPLATE_LOCAL_STORAGE_KEY = "_higharcDesignMaterialTemplate";
const FILENAME_LOCAL_STORAGE_KEY = "_higharcDesignMaterialName";

const LIBRARY_LIST_SRC = "/static/materials/libraryList.json";

const COLOR_LIBRARY_DIR = "/static/materials/colorlibraries/";
const LAYER_CONFIG_LIBRARY_DIR = "/static/materials/layerconfiglibraries/";
const LAYER_CONFIG_DIR = "/static/materials/layerconfigs/";

const TEMPLATE_PREVIEW_LIMIT = 9;
const LIBRARY_PREVIEW_LIMIT = 25;

const THUMBNAIL_WORLD_WIDTH = 36;

const largeSlantsGltfURL = "/static/propsTest/DV_MaterialTest_LargeSlants.glb";

function sortIdsComparator(a: string, b: string): number {
  return a.localeCompare(b, "en", { sensitivity: "accent" });
}

enum UndoBehavior {
  CLEAR_STACK = "CLEAR_STACK",
  UNDOABLE = "UNDOABLE",
}

function layerConfigNameFromFilePath(filePath: string) {
  const path = filePath.split("/").pop();
  assertIsNotUndefined(path);
  return path.split(".")[0];
}

const testLights: SceneLightSettings = {
  toneMappingEnabled: false,
  toneMappingExposure: 1.0,
  hemisphereLight: {
    position: new Vector3(0, 0, 1),
    intensity: 0.7,
    color: new Color(0xbcbcbc),
    groundColor: new Color(0x5e5e5e),
  },
  directionalLight: {
    position: new Vector3(1600, -1500, 1000),
    intensity: 0.8,
    color: new Color(0xfdfdfd),
  },
  ambientLight: {
    intensity: 0.1,
    color: new Color(0xededed),
  },
  fog: {
    color: new Color(0xc1c1c1),
    near: 40000,
    far: 50000,
  },
  probeIrradianceSaturationMultiplier: 0.85,
  sky: {
    topColor: new Color(0xa8e3e6),
    horizonColor: new Color(0xe1f4f5),
    bottomColor: new Color(0xffffff),
    horizonSolidWidth: 0.05,
    horizonGradientWidth: 0.75,
    horizonInclination: 0.0,
    sunCenterBlendAlpha: 0.0,
    sunHaloBlendAlpha: 0.0,
    sunHazeBlendAlpha: 0.0,
  },
  lightProbeSkyHemiLightFactor: 1.0,
  lightProbeSkyHemiLightHorizontal: 0.0,
  exteriorEnvMapEXR: false,
  exteriorEnvMap: "",
};

const defaultPerspectiveCameraSettings: CameraSettings = {
  name: "Default",
  fov: 50,
  ortho: false,
  x: 120,
  y: -250,
  z: 80,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 0,
  upZ: 1,
  cameraNear: 5,
  cameraFar: 10000,
};

const perspectiveCameraSettings: CameraSettings[] = [
  defaultPerspectiveCameraSettings,
  {
    name: "Side",
    fov: 50,
    ortho: false,
    x: 90,
    y: -300,
    z: 20,
    targetX: 0,
    targetY: 0,
    targetZ: 0,
    upX: 0,
    upY: 0,
    upZ: 1,
    cameraNear: 5,
    cameraFar: 10000,
  },
  {
    name: "Diagonal",
    fov: 50,
    ortho: false,
    x: 150,
    y: -150,
    z: 150,
    targetX: 0,
    targetY: 0,
    targetZ: 0,
    upX: 0,
    upY: 0,
    upZ: 1,
    cameraNear: 5,
    cameraFar: 10000,
  },
  {
    // This one has been tuned so that the reflection of the sun in the morning lights preset is visible.
    name: "Morning sun reflection",
    fov: 50,
    ortho: false,
    x: -165,
    y: 145,
    z: 150,
    targetX: 0,
    targetY: 0,
    targetZ: 0,
    upX: 0,
    upY: 0,
    upZ: 1,
    cameraNear: 5,
    cameraFar: 10000,
  },
];

const orthoCameraSettings: CameraSettings = {
  name: "Default",
  fov: 50,
  ortho: true,
  x: 0,
  y: 150,
  z: 0,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 0,
  upZ: 1,
  minOrthoWidth: 200,
  minOrthoHeight: 200,
  cameraNear: 5,
  cameraFar: 10000,
};

interface DesignMaterialEditorState {
  displayedFilename: string;
  heightMapResolution: number;
  mainMaskTexPixelWidth: number;
  detailRenderModeNotice: string;

  availableColorLibraries: string[];
  availableLayerConfigLibraries: string[];
  selectedColorLibraries: string[];
  selectedLayerConfigLibraries: string[];
  allowUsingTemplateOnItsOwn: boolean;

  showPrevPreviewButton: boolean;
  showNextPreviewButton: boolean;
  previewPage: string;

  cameraIndex: number;

  displayGPUMemoryGraph: boolean;
}

interface LibraryList {
  colorLibraries: string[];
  layerConfigLibraries: string[];
}

interface FileMetadata {
  loadedFromPath: string;
  templateName: string;
  layerConfigName: string;
  hoveredName: string;
}

interface LibraryMaterialForPreview {
  name: string;
  params: DesignMaterialParams;
}

const DisplayTypes = [
  "material",
  "geometryConversion",
  "geometryConversionWireframe",
  "exportGeometryConversion",
  "exportGeometryConversionWireframe",
  "heightmap",
  "bakedNormalMap",
  "heightmapScene",
  "heightmapSceneWireframe",
  "libraryPreview",
  "templatePreview",
] as const;

type DisplayType = (typeof DisplayTypes)[number];

type MasonryTrimString = "Base" | "Header" | "Rowlock";

class DesignMaterialEditor extends React.Component<any, DesignMaterialEditorState> {
  private viewerContainer: HTMLDivElement;
  private viewer: THREEViewer | null;
  private viewerRenderer: THREEViewerRenderer;
  private loader: THREEAssetLoader;
  private materialLoader: HTTPMaterialParameterLoader;
  private geometryContainer: THREE.Object3D; // Container that has a rotation handle attached to it.
  private transformControls: TransformControls;
  private displayedContent: THREE.Object3D | undefined;

  private fileMetadata: FileMetadata;

  private disposables: (THREE.BufferGeometry | THREE.CameraHelper)[] = [];

  private materialParamsUndoStack: DesignMaterialParams[] = [];
  private activeMaterialParams: DesignMaterialParams;
  private lastLoadedTemplateParams?: DesignMaterialParams;
  private previewedMaterialSet: LibraryMaterialForPreview[] = []; // For previewing a library of materials.
  private currentPreviewLimit: number = LIBRARY_PREVIEW_LIMIT;
  private previewStartIndex: number = 0;
  private fetchedTextures: { [key: string]: Texture } = {};

  private largeSlantsModel: THREE.Object3D;

  private loadedUserModel: THREE.Object3D;

  private orthographicCamera: boolean = false;

  private repeatingSDF: boolean = defaultDesignMaterialLoadOptions.repeatingSDF;
  private bakeColorTexture: BakeColorTextureMode = defaultDesignMaterialLoadOptions.bakeColorTexture;
  private forceDetailRenderMode: string | null = null; // Use null since dat.gui doesn't support undefined.
  private layerViewMode: string = LayerViewMode[defaultDesignMaterialLoadOptions.layerViewMode];
  private componentViewMode: string = ComponentViewMode[defaultDesignMaterialLoadOptions.componentViewMode];
  private useWebgl2TextureAtlas: boolean = defaultDesignMaterialLoadOptions.useWebgl2TextureAtlas;

  private disposeGUI: () => void;
  private display: DisplayType = "material";
  private geometryType: string = "box";
  private autoTrimVariant: MasonryTrimString = "Base";
  private envPreset: EnvPreset = EnvPreset.WHITEBOX;
  private sceneLights: SceneLightSettings;
  private lightingType: string = "whitebox";
  private groundEnabled: boolean = true;

  private groundMesh: Object3D | null = null;
  private lightProbeGrid: SHLightProbeGrid | null = null;

  private siteGeneration: number = 0; // Incremented whenever the site parameters change so the light probe grid can be updated.
  private lightProbeSiteGeneration: number = -1;

  private material: MeshPhysicalMaterial | null = null;
  private loadedMaterials: MeshPhysicalMaterial[] = [];

  private disposeCtrls: () => void;

  private libraryList: LibraryList;

  private displayedHatchMaterialId;
  private hatchMaterialCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  private thumbnailImgRef: React.RefObject<HTMLImageElement | null>;
  private mouseDownClientX: number;
  private mouseDownClientY: number;

  private geometryCache: { [geometryType: string]: THREE.BufferGeometry } = {};

  private updateSceneRequest: number = 0;
  private updateSceneInFlight: boolean = false;

  constructor(props) {
    super(props);

    this.fileMetadata = {
      loadedFromPath: "",
      templateName: "",
      layerConfigName: "",
      hoveredName: "",
    };

    this.state = {
      displayedFilename: "",
      detailRenderModeNotice: "",
      heightMapResolution: -1,
      mainMaskTexPixelWidth: -1,
      availableColorLibraries: [],
      availableLayerConfigLibraries: [],
      selectedColorLibraries: [],
      selectedLayerConfigLibraries: [],
      allowUsingTemplateOnItsOwn: false,
      showPrevPreviewButton: false,
      showNextPreviewButton: false,
      previewPage: "",

      cameraIndex: 0,

      displayGPUMemoryGraph: false,
    };

    this.hatchMaterialCanvasRef = React.createRef<HTMLCanvasElement | null>();
    this.thumbnailImgRef = React.createRef<HTMLImageElement | null>();
  }

  render() {
    return (
      <div>
        <Head>
          <title>{getPageTitle("Design Material Editor")}</title>
          <meta name="robots" content="noindex" />
        </Head>

        <div
          className={styles.viewer}
          ref={(e) => {
            if (e) {
              this.viewerContainer = e;
            }
          }}
          onMouseMove={(e) => this.mouseMove(e)}
          onMouseDown={(e) => this.mouseDown(e)}
          onMouseUp={(e) => this.mouseUp(e)}
        />
        <div className={styles.librarySidebar}>
          <Select
            value=""
            placeholder="Load material by ID"
            options={(input) => getMaterialPathAutofillOptions(input, true)}
            labelFromValue={(value) => value as string}
            onChange={(value) => {
              void this.loadMaterialPath(value as string);
            }}
          />
          <h3>Template properties</h3>
          <div className={styles.librarySidebarCheckbox}>
            <input
              type="checkbox"
              checked={this.state.allowUsingTemplateOnItsOwn}
              onChange={(event) => {
                this.activeMaterialParams.allowUsingTemplateOnItsOwn = event.target.checked;
                void this.updateSceneAndGUI();
              }}
            ></input>
            Allow using template on its own
          </div>
          <LibraryListAddButton
            categoryName="color libraries"
            availableLibraries={this.state.availableColorLibraries}
            addLibrary={(name) => this.addColorLibrary(name)}
          />
          <LibraryListAddButton
            categoryName="layer config libraries"
            availableLibraries={this.state.availableLayerConfigLibraries}
            addLibrary={(name) => this.addLayerConfigLibrary(name)}
          />
          <div className={styles.librarySidebarScrollableSection}>
            <LibraryList
              categoryName="color libraries"
              selectedLibraries={this.state.selectedColorLibraries}
              removeLibrary={(name) => this.removeColorLibrary(name)}
              showLibrary={(name) => this.showColorLibrary(name)}
            />
            <LibraryList
              categoryName="layer config libraries"
              selectedLibraries={this.state.selectedLayerConfigLibraries}
              removeLibrary={(name) => this.removeLayerConfigLibrary(name)}
              showLibrary={(name) => this.showLayerConfigLibrary(name)}
            />
          </div>
        </div>
        <div className={styles.buttonContainer1}>
          <button
            onClick={() => {
              if (confirm("Are you sure you want to reset?")) {
                this.fileMetadata.loadedFromPath = "";
                this.fileMetadata.templateName = "";
                this.fileMetadata.layerConfigName = "";
                this.fileMetadata.hoveredName = "";
                this.updateDisplayedFilename();
                this.exitLibraryDisplay();
                void this.updateMaterialParams(this.getDefaultMaterialParams(), undefined, UndoBehavior.CLEAR_STACK);
              }
            }}
          >
            Reset
          </button>
          <button onClick={() => this.tryImportMaterial()}>Open template...</button>
          <button
            onClick={() => {
              if (
                !this.fileMetadata.layerConfigName ||
                confirm(
                  "Saving the template will replace template values with layer config loaded from " +
                    this.fileMetadata.layerConfigName +
                    ".",
                )
              ) {
                const paramsCopy = structuredClone(this.activeMaterialParams);
                sdfPatternsListTrim(paramsCopy);
                downloadFile(
                  this.fileMetadata.templateName
                    ? this.fileMetadata.templateName + ".default.json"
                    : "SavedDesignMaterial.default.json",
                  formatMaterialJSON(paramsCopy),
                  "application/json",
                );
              }
            }}
          >
            Save template...
          </button>
          {this.display === "material" ? (
            <button onClick={() => this.resetToTemplate()}>Reset layers to template values</button>
          ) : (
            ""
          )}
          <button className={styles.uiButtonColorCodeParent} onClick={() => this.tryImportLayerConfig()}>
            <span className={styles.uiButtonColorCode} style={{ backgroundColor: layersUIColorLight }}></span>
            Open layers...
          </button>
          <button
            className={styles.uiButtonColorCodeParent}
            onClick={() => {
              const paramsCopy = extractLayerConfig(this.activeMaterialParams);
              let layerFilename = this.fileMetadata.layerConfigName
                ? this.fileMetadata.layerConfigName
                : "SavedConfig.layers.json";
              if (!layerFilename.endsWith(".layers.json")) {
                if (layerFilename.endsWith(".json")) {
                  layerFilename = layerFilename.replace(/\.json$/, ".layers.json");
                } else {
                  layerFilename = layerFilename + ".layers.json";
                }
              }
              downloadFile(layerFilename, formatMaterialJSON(paramsCopy), "application/json");
            }}
          >
            <span className={styles.uiButtonColorCode} style={{ backgroundColor: layersUIColorLight }}></span>
            Save layers...
          </button>
          <button onClick={() => this.previewAllTemplates()}>Preview all templates</button>
          <div className={styles.buttonRowText}>{this.state.displayedFilename}</div>
          <button
            onClick={() => {
              window.open("./material-id");
            }}
          >
            Debug Material IDs
          </button>
        </div>

        <div className={styles.buttonContainer2}>
          <div className={styles.previewCanvasWithControls + " " + styles.hatchMaterialPreview}>
            <canvas ref={this.hatchMaterialCanvasRef} width="256" height="256"></canvas>
          </div>
          <img className={styles.thumbnailPreview} ref={this.thumbnailImgRef} width="256" height="256"></img>
          <div
            style={{
              width: "200px",
              display: "flex",
              flexDirection: "column",
              zIndex: 10,
              background: "white",
            }}
          >
            <button onClick={() => this.setState({ displayGPUMemoryGraph: !this.state.displayGPUMemoryGraph })}>
              GPU Memory History {this.state.displayGPUMemoryGraph ? "On" : "Off"}
            </button>
            {this.state.displayGPUMemoryGraph ? <GLMemoryHistoryGraph /> : null}
          </div>
          <button
            onClick={() => {
              window.open("./design-material-docs");
            }}
          >
            Documentation
          </button>
          <button onClick={() => this.tryImportGeometry()}>Open Custom Geometry</button>
          <button onClick={() => this.importEnvMap()}>Open Custom Env Map</button>
          <button onClick={() => this.tryAnalyzeFiles()}>Analyze</button>
          {this.state.showPrevPreviewButton ? <button onClick={() => this.previousPreview()}>Previous</button> : ""}
          <span>{this.state.previewPage}</span>
          {this.state.showNextPreviewButton ? <button onClick={() => this.nextPreview()}>Next</button> : ""}
          {this.display === "libraryPreview" ? (
            <button
              onClick={() => {
                this.exitLibraryDisplay();
                void this.updateSceneAndGUI();
              }}
            >
              Exit Library
            </button>
          ) : (
            ""
          )}
          <div className={styles.buttonRowText}>
            {this.state.detailRenderModeNotice.length > 0 ? (
              <span className={styles.warning}>{this.state.detailRenderModeNotice}</span>
            ) : (
              ""
            )}
            {this.state.heightMapResolution > 0 ? ` Height map resolution: ${this.state.heightMapResolution}.` : ""}
            {this.state.mainMaskTexPixelWidth > 0
              ? ` Main mask 1 pixel width = ${this.state.mainMaskTexPixelWidth} world inches.`
              : ""}
          </div>
        </div>
      </div>
    );
  }

  private mouseMove(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!this.viewer || !this.viewerRenderer) {
      return;
    }
    if (e.target !== this.viewerRenderer.canvas) {
      return;
    }
    const intersections = this.viewer.raycast(e.clientX, e.clientY);
    for (const intersection of intersections) {
      if (intersection.object.userData.hoverName) {
        this.fileMetadata.hoveredName = intersection.object.userData.hoverName;
        this.updateDisplayedFilename();
        return;
      }
    }
    this.fileMetadata.hoveredName = "";
    this.updateDisplayedFilename();
  }

  private mouseDown(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!this.viewer || !this.viewerRenderer) {
      return;
    }
    if (e.target !== this.viewerRenderer.canvas) {
      return;
    }
    this.mouseDownClientX = e.clientX;
    this.mouseDownClientY = e.clientY;
  }

  private mouseUp(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!this.viewer || !this.viewerRenderer) {
      return;
    }
    if (e.target !== this.viewerRenderer.canvas) {
      return;
    }
    const dragDist2 = Math.pow(e.clientX - this.mouseDownClientX, 2) + Math.pow(e.clientY - this.mouseDownClientY, 2);
    if (dragDist2 > 25) {
      return;
    }
    const intersections = this.viewer.raycast(e.clientX, e.clientY);
    for (const intersection of intersections) {
      if (intersection.object.userData.materialParamsOnClick) {
        this.fileMetadata.hoveredName = "";
        if (this.display === "libraryPreview") {
          this.fileMetadata.layerConfigName = intersection.object.userData.hoverName;
        } else if (this.display === "templatePreview") {
          this.fileMetadata.loadedFromPath = "";
          this.fileMetadata.templateName = intersection.object.userData.hoverName;
          this.fileMetadata.layerConfigName = "";
        }
        this.display = "material";
        void this.updateMaterialParams(
          intersection.object.userData.materialParamsOnClick,
          this.lastLoadedTemplateParams,
          UndoBehavior.CLEAR_STACK,
        );
        this.updateDisplayedFilename();
        break;
      }
    }
  }

  async componentDidMount() {
    if (this.viewer) {
      return;
    }

    this.sceneLights = { ...envPresetSceneLights[this.envPreset] };

    this.viewer = new THREEViewer({
      container: this.viewerContainer,
      options: {
        gui: true,
        materialFactoryGUI: false,
        ssfxRenderer: true,
        animate: true,
        sceneLights: this.sceneLights,
        debugFeaturesEnabled: true,
        cameraSettings: defaultPerspectiveCameraSettings,
        logGLResourceUsage: true,
      },
    });
    this.materialLoader = new HTTPMaterialParameterLoader();

    this.loader = THREEAssetLoader.instance;
    const libraryListStr = await this.loader.fetchJson(LIBRARY_LIST_SRC, true);
    if (libraryListStr !== undefined) {
      this.libraryList = JSON.parse(libraryListStr);
      this.setState({
        availableColorLibraries: this.libraryList.colorLibraries,
        availableLayerConfigLibraries: this.libraryList.layerConfigLibraries,
      });
    }

    this.geometryContainer = new Object3D();
    this.viewer.scene.add(this.geometryContainer);
    this.transformControls = new TransformControls(this.viewer.camera, this.viewer.container);
    this.viewer.scene.add(this.transformControls);
    this.transformControls.setMode("rotate");
    this.transformControls.addEventListener("change", () => {
      this.geometryContainer.updateMatrix();
      this.geometryContainer.updateMatrixWorld();
      this.transformControls.updateMatrixWorld();

      // Check viewer's existence to support hot reload.
      if (this.viewer) {
        this.viewer.scene.markShadowMapsForUpdate();
        this.viewer.scene.markRenderingNeedsUpdate();
      }
    });

    this.transformControls.addEventListener("dragging-changed", (event) => {
      // Disable camera controls when interacting with the transform controls.
      assertIsNotNull(this.viewer);
      if (event.value) {
        this.viewer.controls.lock("dme-transform-drag");
      } else {
        this.viewer.controls.unlock("dme-transform-drag");
      }
    });
    this.viewer.controls.addEventListener("change", () => this.transformControls.updateMatrixWorld());

    const onKeyDown = (event) => {
      if (event.key === "z" && (event.ctrlKey || event.metaKey)) {
        if (this.materialParamsUndoStack.length > 1) {
          this.materialParamsUndoStack.pop();
          const paramsToApply = this.materialParamsUndoStack[this.materialParamsUndoStack.length - 1];
          const patch = diffObjects(this.activeMaterialParams, paramsToApply);
          applyPatch(this.activeMaterialParams, patch);
          this.saveParamsToLocalStorage();
          this.viewer?.viewerRenderer?.gui?.updateDisplay();
          this.updateScene();
        }
        event.preventDefault();
        return;
      }
      if (
        (document.activeElement instanceof HTMLInputElement &&
          (document.activeElement as HTMLInputElement).type === "text") ||
        document.activeElement instanceof HTMLTextAreaElement
      ) {
        return;
      }
      if (event.key === "o" && this.transformControls.object === undefined) {
        this.transformControls.attach(this.geometryContainer);
        this.transformControls.updateMatrixWorld();
        if (this.viewer) {
          this.viewer.scene.markRenderingNeedsUpdate();
        }
      }
    };
    const onKeyUp = (event) => {
      if (event.key === "o") {
        this.transformControls.detach();
        if (this.viewer) {
          this.viewer.scene.markRenderingNeedsUpdate();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown, false);
    window.addEventListener("keyup", onKeyUp, false);

    this.disposeCtrls = () => {
      window.removeEventListener("keydown", onKeyDown, false);
      window.removeEventListener("keydown", onKeyUp, false);
    };

    await this.viewer.guiPromise;

    this.viewerRenderer = this.viewer.viewerRenderer;

    this.fetchedTextures = await loadAllMaterialEditorTextures();

    this.largeSlantsModel = await this.loader.loadGLTF(largeSlantsGltfURL);

    await this.initGround();

    await this.initLightProbe();

    this.forceDetailRenderMode = null;

    const serializedParams = localStorage.getItem(PARAMS_LOCAL_STORAGE_KEY);
    if (!serializedParams) {
      await this.updateMaterialParams(this.getDefaultMaterialParams(), undefined, UndoBehavior.CLEAR_STACK);
      return;
    }

    // Note that it's important to load the filename before updateSceneGUIFromMaterialParams(), since that will trigger saving the filename.
    const serializedFileMetadata = localStorage.getItem(FILENAME_LOCAL_STORAGE_KEY);
    if (serializedFileMetadata) {
      try {
        this.fileMetadata = JSON.parse(serializedFileMetadata);
      } catch (e) {
        this.fileMetadata.templateName = serializedFileMetadata;
      }
      this.updateDisplayedFilename();
    }

    const params = parseDesignMaterialParams(serializedParams);

    let templateParams;
    const serializedTemplateParams = localStorage.getItem(TEMPLATE_LOCAL_STORAGE_KEY);
    if (serializedTemplateParams) {
      templateParams = parseDesignMaterialParams(serializedTemplateParams);
    }

    await this.updateMaterialParams(
      params ?? this.getDefaultMaterialParams(),
      templateParams,
      UndoBehavior.CLEAR_STACK,
    );
  }

  async initGround() {
    assertIsNotNull(this.viewer);
    if (!this.groundMesh) {
      const prop = defaultGroundProp;
      prop.z = -100;

      const matParams = await this.materialLoader.fetchDesignMaterialParams(prop.material);
      assertIsNotNull(matParams);

      const mat = await designMaterialNew(
        matParams,
        this.viewerRenderer.renderer,
        (url, _overrideFormat) => [
          this.fetchedTextures[url],
          new Promise((resolve, _reject) => setTimeout(resolve, 0)),
        ],
        this.viewer.hasSSFXRenderer,
        undefined,
        defaultDesignMaterialLoadOptions,
      );
      mat.name = "ground-grass";

      this.groundMesh = await convert.dracoToTHREE(
        this.loader,
        prop.url,
        mat,
        DRACO_ASSET_SCALE,
        prop.scaleUV,
        prop.scaleUVMult,
      );
      this.groundMesh.name = "ground";

      this.groundMesh.position.set(prop.x, prop.y, prop.z);
      this.groundMesh.updateMatrix();
      this.groundMesh.updateMatrixWorld();

      assignToSiteLayer(this.groundMesh);

      this.groundMesh.castShadow = false;
      this.groundMesh.receiveShadow = true;
    }
    this.viewer.scene.staticContent.add(this.groundMesh);
    this.groundMesh.visible = this.groundEnabled;
  }

  async initLightProbe() {
    assertIsNotNull(this.viewer);
    if (!this.lightProbeGrid) {
      const services = this.viewerRenderer.services;

      // Generate a single light probe for the site. It will only have the site layer rendered into it.
      const generatePmrem = true;

      this.lightProbeGrid = await services.createLightProbeGrid(
        1,
        1,
        1,
        "site",
        generatePmrem,
        new Vector3(10000, 10000, 200),
      );
      this.lightProbeGrid.renderLayers.set(SITE_LAYER);
      this.lightProbeGrid.position.set(0, 0, 100);
      this.lightProbeGrid.scale.set(250, 250, 250);
      this.lightProbeGrid.layers.enableAll();
      this.lightProbeGrid.probesNeedUpdate = true;
    }
    this.viewer.scene.staticContent.add(this.lightProbeGrid);
    this.syncLightProbeGridUseForBounceLighting();
  }

  /** When using WHITEBOX scene lights, probes still run but must not contribute bounce lighting. */
  private syncLightProbeGridUseForBounceLighting() {
    assertIsNotNull(this.viewer);
    if (!this.lightProbeGrid) {
      return;
    }
    this.lightProbeGrid.useForGlobalIrradiance = this.envPreset !== EnvPreset.WHITEBOX;
    this.viewer.scene.updateLightProbesList();
  }

  componentWillUnmount() {
    if (this.viewer) {
      this.viewer.scene.clear();
      this.viewer.dispose();
      this.viewer = null;
    }
    if (this.disposeCtrls) {
      this.disposeCtrls();
    }
  }

  private exitLibraryDisplay() {
    if (this.display === "libraryPreview" || this.display === "templatePreview") {
      this.display = "material";
      this.previewedMaterialSet = [];
    }
  }

  private openFiles(allowMultiple, accept = "application/json,.json"): Promise<FileList> {
    return new Promise((resolve, reject) => {
      const d = document.createElement("input") as HTMLInputElement;
      d.type = "file";
      if (allowMultiple) {
        d.multiple = true;
      }
      d.accept = accept;
      document.body.appendChild(d);
      d.onchange = () => {
        if (!d.files || d.files.length === 0) {
          reject();
        } else {
          resolve(d.files);
        }
      };
      d.click();
      document.body.removeChild(d);
    });
  }

  private async importEnvMap() {
    const files = await this.openFiles(false, "image/*,.exr");
    const reader = new FileReader();
    reader.onloadend = (e) => {
      if (e.loaded) {
        const dataUrl = reader.result as string;
        const isEXR = files[0].name.endsWith(".exr");
        this.sceneLights.exteriorEnvMap = dataUrl;
        this.sceneLights.exteriorEnvMapEXR = isEXR;
        assertIsNotNull(this.viewer);
        assertIsNotUndefined(this.viewer.scene.ssfxRendererScene);
        void this.viewer.scene.ssfxRendererScene.sceneLightsManager.setEnvMap(dataUrl, isEXR);
      }
    };

    reader.readAsDataURL(files[0]);
  }

  private async tryImportMaterial() {
    const files = await this.openFiles(false);
    const reader = new FileReader();
    reader.onloadend = (e) => {
      if (e.loaded) {
        const params = parseDesignMaterialParams(reader.result as string);

        if (!params) {
          alert("Could not load material. Check the console for errors.");
        }

        if (params) {
          this.fileMetadata.templateName = files[0].name.replace(/\..+$/, "");
          this.fileMetadata.layerConfigName = "";
          this.fileMetadata.loadedFromPath = "";
          this.fileMetadata.hoveredName = "";
          this.updateDisplayedFilename();
          this.exitLibraryDisplay();
          void this.updateMaterialParams(params, params, UndoBehavior.CLEAR_STACK);
        }
      }
    };

    reader.readAsText(files[0]);
  }

  private resetToTemplate() {
    const params = structuredClone(this.activeMaterialParams);
    assertIsNotUndefined(this.lastLoadedTemplateParams);
    applyLayerConfig(params, extractLayerConfig(this.lastLoadedTemplateParams));
    this.fileMetadata.layerConfigName = "";
    void this.updateMaterialParams(params, this.lastLoadedTemplateParams, UndoBehavior.UNDOABLE);
  }

  private async tryImportLayerConfig() {
    const files = await this.openFiles(false);
    // create reader
    const reader = new FileReader();
    reader.onloadend = (e) => {
      if (e.loaded) {
        const params = parseLayerConfig(reader.result as string);

        if (!params) {
          alert("Could not load layer config. Check the console for errors.");
        }

        if (params) {
          this.fileMetadata.layerConfigName = files[0].name;
          this.fileMetadata.loadedFromPath = "";
          this.fileMetadata.hoveredName = "";
          this.updateDisplayedFilename();
          this.exitLibraryDisplay();
          void this.updateLayerConfigParams(params);
        }
      }
    };

    reader.readAsText(files[0]);
  }

  private async loadMaterialPath(libraryPath: string) {
    const materialSrc: MaterialSrc | undefined = materialSrcFromPathString(libraryPath);
    assertIsNotUndefined(materialSrc);
    const pathComponents = libraryPath.split(".");
    const templateParams = await this.materialLoader.fetchDesignMaterialParams(
      { templateId: materialSrc.templateId },
      true,
    );
    const params = await this.materialLoader.fetchDesignMaterialParams(materialSrc, true);
    if (templateParams && params) {
      this.fileMetadata.templateName = pathComponents[0];
      if (materialSrc.layerConfigId !== undefined) {
        const layerConfigJsonPath = LayerConfigIdToJson[materialSrc.layerConfigId];
        this.fileMetadata.layerConfigName = layerConfigNameFromFilePath(layerConfigJsonPath);
      } else {
        this.fileMetadata.layerConfigName = "";
      }
      this.fileMetadata.loadedFromPath = libraryPath;
      this.fileMetadata.hoveredName = "";
      this.updateDisplayedFilename();
      this.exitLibraryDisplay();
      void this.updateMaterialParams(params, templateParams, UndoBehavior.CLEAR_STACK);
    }
  }

  private async tryImportGeometry() {
    const files = await this.openFiles(false, "model/gltf-binary,.glb");
    const reader = new FileReader();
    reader.onloadend = (e) => {
      if (e.loaded) {
        this.loader.gltfLoader.parse(reader.result as ArrayBuffer, "", ({ scene }) => {
          this.loadedUserModel = THREEAssetLoader.processGLTFScene(scene);
          this.geometryType = "custom";
          this.updateScene();
        });
      }
    };
    reader.readAsArrayBuffer(files[0]);
  }

  private async tryAnalyzeFiles() {
    const files = await this.openFiles(true);
    const uniqueHashes = new Set();
    let readCount = 0;
    for (let i = 0; i < files.length; ++i) {
      ((file) => {
        const reader = new FileReader();
        reader.onloadend = async (e) => {
          assertIsNotNull(this.viewer);
          if (e.loaded) {
            const params = parseDesignMaterialParams(reader.result as string);

            if (params) {
              const material = await designMaterialNew(
                params,
                this.viewerRenderer.renderer,
                (url, _overrideFormat) => [
                  this.fetchedTextures[url],
                  new Promise((resolve, _reject) => setTimeout(resolve, 0)),
                ],
                this.viewer.hasSSFXRenderer,
                undefined,
                defaultDesignMaterialLoadOptions,
              );
              let materialHash = material.customProgramCacheKey();
              const defines = material.defines ?? {};
              for (const key of Object.keys(defines).sort()) {
                materialHash += ";" + key + "=" + defines[key];
              }
              materialHash += ";" + (material instanceof MeshPhysicalMaterial ? "physical" : "");
              uniqueHashes.add(materialHash);
            }
            ++readCount;
            if (readCount === files.length) {
              console.log("Number of materials: " + files.length + ". Number of unique shaders: " + uniqueHashes.size);
            }
          }
        };
        reader.readAsText(file);
      })(files[i]);
    }
  }

  private saveParamsToLocalStorage() {
    if (!this.activeMaterialParams) {
      return;
    }

    // copy the params object as we will append the associated texture urls
    const paramsCopy = structuredClone(this.activeMaterialParams);

    localStorage.setItem(PARAMS_LOCAL_STORAGE_KEY, formatMaterialJSON(paramsCopy));

    if (this.lastLoadedTemplateParams) {
      const templateParamsCopy = structuredClone(this.lastLoadedTemplateParams);

      localStorage.setItem(TEMPLATE_LOCAL_STORAGE_KEY, formatMaterialJSON(templateParamsCopy));
    } else {
      localStorage.removeItem(TEMPLATE_LOCAL_STORAGE_KEY);
    }

    localStorage.setItem(FILENAME_LOCAL_STORAGE_KEY, JSON.stringify(this.fileMetadata));
  }

  private getDefaultMaterialParams(): DesignMaterialParams {
    const params = cloneDefaultMaterialParams();
    return params;
  }

  private addColorLibrary(libName: string) {
    if (this.libraryList === undefined || this.libraryList.colorLibraries.indexOf(libName) < 0) {
      return;
    }
    if (this.activeMaterialParams.compatibleColorLibraries.indexOf(libName) >= 0) {
      return;
    }
    this.activeMaterialParams.compatibleColorLibraries.push(libName);
    this.setState({ selectedColorLibraries: this.activeMaterialParams.compatibleColorLibraries });
    this.saveParamsToLocalStorage();
  }

  private addLayerConfigLibrary(libName: string) {
    if (this.libraryList === undefined || this.libraryList.layerConfigLibraries.indexOf(libName) < 0) {
      return;
    }
    if (this.activeMaterialParams.compatibleLayerConfigLibraries.indexOf(libName) >= 0) {
      return;
    }
    this.activeMaterialParams.compatibleLayerConfigLibraries.push(libName);
    this.setState({ selectedLayerConfigLibraries: this.activeMaterialParams.compatibleLayerConfigLibraries });
    this.saveParamsToLocalStorage();
  }

  private removeColorLibrary(libName: string) {
    const index = this.activeMaterialParams.compatibleColorLibraries.indexOf(libName);
    if (index < 0) {
      return;
    }
    this.activeMaterialParams.compatibleColorLibraries.splice(index, 1);
    this.setState({ selectedColorLibraries: this.activeMaterialParams.compatibleColorLibraries });
    this.saveParamsToLocalStorage();
  }

  private removeLayerConfigLibrary(libName: string) {
    const index = this.activeMaterialParams.compatibleLayerConfigLibraries.indexOf(libName);
    if (index < 0) {
      return;
    }
    this.activeMaterialParams.compatibleLayerConfigLibraries.splice(index, 1);
    this.setState({ selectedLayerConfigLibraries: this.activeMaterialParams.compatibleLayerConfigLibraries });
    this.saveParamsToLocalStorage();
  }

  private async previewAllTemplates() {
    this.previewedMaterialSet = [];
    const sortedTemplateNames = Object.values(TemplateIdToName).sort(sortIdsComparator);
    for (const name of sortedTemplateNames) {
      const fileURL = "/static/materials/templates/" + name + ".default.json";
      const jsonStr = await this.loader.fetchJson(fileURL, true);
      assertIsNotUndefined(jsonStr);
      const params = parseDesignMaterialParams(jsonStr);
      assertIsNotUndefined(params);
      this.previewedMaterialSet.push({
        name,
        params,
      });
    }

    this.display = "templatePreview";
    this.previewStartIndex = 0;
    this.currentPreviewLimit = TEMPLATE_PREVIEW_LIMIT;
    this.updateScene();
  }

  private async showColorLibrary(libName: string) {
    const src = `${COLOR_LIBRARY_DIR}${libName}.clib.json`;
    const jsonStr = await this.loader.fetchJson(src, true);
    assertIsNotUndefined(jsonStr);
    const library: ColorLibraryParams = JSON.parse(jsonStr);

    this.previewedMaterialSet = [];

    const baseParams: DesignMaterialParams = structuredClone(this.activeMaterialParams);
    if (this.fileMetadata.layerConfigName && this.lastLoadedTemplateParams) {
      applyLayerConfig(baseParams, extractLayerConfig(this.lastLoadedTemplateParams));
    }

    const sortedColors = Object.keys(library).sort(sortIdsComparator);
    for (const colorName of sortedColors) {
      const params = structuredClone(baseParams);
      params.color = library[colorName];
      this.previewedMaterialSet.push({
        name: colorName,
        params,
      });
    }

    this.display = "libraryPreview";
    this.previewStartIndex = 0;
    this.currentPreviewLimit = LIBRARY_PREVIEW_LIMIT;
    this.updateScene();
  }

  private async showLayerConfigLibrary(libName: string) {
    const src = `${LAYER_CONFIG_LIBRARY_DIR}${libName}.llib.json`;
    const jsonStr = await this.loader.fetchJson(src, true);
    assertIsNotUndefined(jsonStr);
    const library: LayerConfigLibraryParams = JSON.parse(jsonStr);

    this.previewedMaterialSet = [];

    const sortedLayerConfigNames = library.sort(sortIdsComparator);
    for (const layerConfigName of sortedLayerConfigNames) {
      const params: DesignMaterialParams = structuredClone(this.activeMaterialParams);
      const layerSrc = `${LAYER_CONFIG_DIR}${layerConfigName}.layers.json`;
      const layerJsonStr = await this.loader.fetchJson(layerSrc, true);
      if (layerJsonStr === undefined) {
        console.error("Error loading layer config " + layerConfigName);
        continue;
      }
      const layerConfig = parseLayerConfig(layerJsonStr);
      assertIsNotUndefined(layerConfig);
      applyLayerConfig(params, layerConfig);
      this.previewedMaterialSet.push({
        name: layerConfigName,
        params,
      });
    }

    this.display = "libraryPreview";
    this.previewStartIndex = 0;
    this.currentPreviewLimit = LIBRARY_PREVIEW_LIMIT;
    this.updateScene();
  }

  private updateDisplayedFilename() {
    let displayedFilename = "";
    if (this.display === "templatePreview") {
      displayedFilename = this.fileMetadata.hoveredName;
    } else if (this.display === "libraryPreview") {
      displayedFilename = this.fileMetadata.templateName;
      if (this.fileMetadata.hoveredName !== "") {
        displayedFilename += " + hovered: " + this.fileMetadata.hoveredName;
      }
    } else {
      const layerConfigNameInPath =
        this.fileMetadata.layerConfigName !== "" &&
        this.fileMetadata.loadedFromPath.endsWith(this.fileMetadata.layerConfigName);
      if (
        this.fileMetadata.loadedFromPath !== "" &&
        (this.fileMetadata.layerConfigName === "" || layerConfigNameInPath)
      ) {
        displayedFilename = this.fileMetadata.loadedFromPath;
      } else {
        displayedFilename = this.fileMetadata.templateName;
      }

      if (this.fileMetadata.layerConfigName !== "" && !layerConfigNameInPath) {
        displayedFilename += " + " + this.fileMetadata.layerConfigName;
      }
    }
    this.setState({ displayedFilename });
  }

  private processMaterialGeometry(obj: THREE.Object3D) {
    obj.traverse((child) => {
      child.castShadow = true;
      child.receiveShadow = true;
      assignToBuildingExteriorLayer(child); // So that the geometry is not rendered into the lightprobe.
    });
    return obj;
  }

  private getLoadedModelForDisplay(model: Object3D, material: THREE.Material): Object3D {
    const obj = model.clone();
    obj.traverse((child) => {
      if (child instanceof Mesh) {
        child.material = material;
      }
    });
    obj.rotation.set(0, 0, Math.PI / 2);
    obj.scale.set(GLTF_ASSET_SCALE, GLTF_ASSET_SCALE, GLTF_ASSET_SCALE);
    return obj;
  }

  private getAndCacheBasicGeo(geometryType: string): THREE.BufferGeometry {
    if (this.geometryCache[geometryType]) {
      return this.geometryCache[geometryType];
    }
    let geo: THREE.BufferGeometry;
    let uvMultiplier = BOX_GEOMETRY_SIZE;
    if (geometryType === "sphere") {
      geo = new SphereGeometry(SPHERE_GEOMETRY_RADIUS, 80, 80);
    } else if (geometryType === "plane") {
      geo = new DoubleSidedPlaneGeometry(BOX_GEOMETRY_SIZE, BOX_GEOMETRY_SIZE);
    } else if (geometryType === "largePlane") {
      geo = new DoubleSidedPlaneGeometry(LARGE_PLANE_GEOMETRY_SIZE, LARGE_PLANE_GEOMETRY_SIZE);
      uvMultiplier *= LARGE_PLANE_GEOMETRY_SIZE / BOX_GEOMETRY_SIZE;
    } else {
      geo = new BoxGeometry(BOX_GEOMETRY_SIZE, BOX_GEOMETRY_SIZE, BOX_GEOMETRY_SIZE);
    }

    convert.scaleBufferGeometryUVs(geo, uvMultiplier);
    geo.userData.isCached = true;

    this.geometryCache[geometryType] = geo;

    return geo;
  }

  private getMaterialPreviewMesh(material): Object3D {
    if (this.geometryType === "largeSlants") {
      return this.getLoadedModelForDisplay(this.largeSlantsModel, material);
    } else if (this.geometryType === "custom") {
      if (this.loadedUserModel) {
        return this.getLoadedModelForDisplay(this.loadedUserModel, material);
      } else {
        console.log("No user geometry loaded!");
      }
    }

    const geo: THREE.BufferGeometry = this.getAndCacheBasicGeo(this.geometryType);

    const mesh = new Mesh(geo, material);
    mesh.name = "materialPreviewMesh";

    if (this.geometryType === "plane" || this.geometryType === "largePlane") {
      mesh.rotation.set(0, 0, Math.PI / 2);
    } else {
      mesh.rotation.set(Math.PI / 2, Math.PI / 2, 0);
    }
    return mesh;
  }

  private removeDisplayedContent() {
    if (this.displayedContent) {
      assertIsNotNull(this.displayedContent.parent);
      this.displayedContent.parent.remove(this.displayedContent);
      this.displayedContent = undefined;
    }
  }

  private displayContent(displayedContent: THREE.Object3D | undefined, inGeometryContainer: boolean) {
    this.removeDisplayedContent();
    assertIsNotNull(this.viewer);
    this.displayedContent = displayedContent;
    if (displayedContent !== undefined) {
      if (inGeometryContainer) {
        this.geometryContainer.add(displayedContent);
      } else {
        this.viewer.scene.add(displayedContent);
      }
      displayedContent.updateMatrixWorld();
    }
    this.viewer.scene.markShadowMapsForUpdate();
    this.viewer.scene.markRenderingNeedsUpdate();
  }

  private updateScene() {
    // This makes sure that there's only one asynchronous updateScene in progress at any given time, so that:
    // 1. we can clean up memory used by previous materials before generating new ones.
    // 2. we don't end up queuing extra redundant work.
    ++this.updateSceneRequest;
    if (!this.updateSceneInFlight) {
      void this.updateSceneAsync();
    }
  }

  private async updateSceneAsync() {
    this.updateSceneInFlight = true;
    const fulfilledUpdateRequest = this.updateSceneRequest;
    const sceneLightingModeSettings = {
      interiorAmbientLightEnabled: false,
      exteriorLightProbesEnabled: this.envPreset !== EnvPreset.WHITEBOX,
    };
    assertIsNotNull(this.viewer);
    assertIsNotUndefined(this.viewer.scene.ssfxRendererScene);
    await this.viewer.scene.ssfxRendererScene.sceneLightsManager.renderSceneLights(
      this.sceneLights,
      sceneLightingModeSettings,
    );
    this.viewer.scene.ssfxRendererScene.sceneLightsManager.initGUI();
    this.syncLightProbeGridUseForBounceLighting();

    const showPreview = this.display === "libraryPreview" || this.display === "templatePreview";
    const previewPage = showPreview
      ? `${Math.floor(this.previewStartIndex / this.currentPreviewLimit) + 1} / ${Math.ceil(
          this.previewedMaterialSet.length / this.currentPreviewLimit,
        )}`
      : "";
    this.setState({
      selectedColorLibraries: this.activeMaterialParams.compatibleColorLibraries,
      selectedLayerConfigLibraries: this.activeMaterialParams.compatibleLayerConfigLibraries,
      allowUsingTemplateOnItsOwn: this.activeMaterialParams.allowUsingTemplateOnItsOwn,
      showPrevPreviewButton: showPreview,
      showNextPreviewButton: showPreview,
      previewPage,
    });

    void (async () => {
      const loadMaterialId = this.activeMaterialParams.hatchMaterialId;
      if (!this.hatchMaterialCanvasRef.current) {
        // Possible if the page redirected to login screen during init
        return;
      }
      const hatchMaterialCtx = this.hatchMaterialCanvasRef.current.getContext("2d");
      if (!hatchMaterialCtx) {
        return;
      }

      const hatchPreviewAabb = aabb2New(0, 0, hatchMaterialCtx.canvas.width, hatchMaterialCtx.canvas.height);

      if (loadMaterialId === "Auto") {
        const startTime = performance.now();
        const hatchMaterial = hatchMaterialFromDesignMaterial(preprocessMaterialParams(this.activeMaterialParams));
        const endTime = performance.now();
        if (endTime - startTime > 20) {
          console.log("Generating hatch material on main thread took: " + (endTime - startTime) + "ms");
        }
        hatchMaterialCtx.fillStyle = "white";
        hatchMaterialCtx.fillRect(0, 0, hatchMaterialCtx.canvas.width, hatchMaterialCtx.canvas.height);
        hatchMaterialCtx.strokeStyle = "black";
        fillHatchLinesOnCanvas(
          hatchMaterialCtx,
          hatchMaterial,
          hatchPreviewAabb,
          THUMBNAIL_WORLD_WIDTH,
          -THUMBNAIL_WORLD_WIDTH * 0.5,
          -THUMBNAIL_WORLD_WIDTH * 0.5,
        );
        this.displayedHatchMaterialId = this.activeMaterialParams.hatchMaterialId;
        return;
      }
      if (loadMaterialId === this.displayedHatchMaterialId) {
        return;
      }
      if (loadMaterialId !== "") {
        let hatchMaterial;
        try {
          hatchMaterial = await defaultHatchMaterialFactory.getLibraryHatchMaterial(HatchMaterialId[loadMaterialId]);
        } catch (e) {
          hatchMaterialCtx.fillStyle = "white";
          hatchMaterialCtx.fillRect(0, 0, hatchMaterialCtx.canvas.width, hatchMaterialCtx.canvas.height);
          hatchMaterialCtx.fillStyle = "red";
          hatchMaterialCtx.textAlign = "center";
          hatchMaterialCtx.fillText(
            "HATCH LOADING FAILED",
            hatchMaterialCtx.canvas.width * 0.5,
            hatchMaterialCtx.canvas.height * 0.5,
          );
          console.error(e);
          return;
        }
        if (
          this.activeMaterialParams.hatchMaterialId !== loadMaterialId ||
          this.displayedHatchMaterialId === loadMaterialId
        ) {
          // Abort in case the material has been switched to another material id while we were waiting for loading, or another
          // invocation of this function finished drawing the same hatch material first.
          return;
        }
        hatchMaterialCtx.fillStyle = "white";
        hatchMaterialCtx.fillRect(0, 0, hatchMaterialCtx.canvas.width, hatchMaterialCtx.canvas.height);
        hatchMaterialCtx.strokeStyle = "black";
        fillHatchLinesOnCanvas(
          hatchMaterialCtx,
          hatchMaterial,
          hatchPreviewAabb,
          THUMBNAIL_WORLD_WIDTH,
          -THUMBNAIL_WORLD_WIDTH * 0.5,
          -THUMBNAIL_WORLD_WIDTH * 0.5,
        );
      } else {
        hatchMaterialCtx.fillStyle = "white";
        hatchMaterialCtx.fillRect(0, 0, hatchMaterialCtx.canvas.width, hatchMaterialCtx.canvas.height);
      }
      this.displayedHatchMaterialId = loadMaterialId;
    })();

    if (this.orthographicCamera !== this.viewer.camera instanceof OrthographicCamera) {
      if (this.orthographicCamera) {
        this.viewer.setCameraSettings(orthoCameraSettings);
      } else {
        this.viewer.setCameraSettings(defaultPerspectiveCameraSettings);
      }
      this.refreshCameraControls();
    }

    const loadOptions = this.getLoadOptions();

    const prevLoadedMaterials = this.loadedMaterials.splice(0, this.loadedMaterials.length);

    if (heightMapCacheCount(this.viewerRenderer.renderer) > 10) {
      const heightMapsInUse: HeightMap[] = [];
      for (const material of prevLoadedMaterials) {
        if (material.userData.heightMap !== undefined) {
          heightMapsInUse.push(material.userData.heightMap);
        }
      }
      await forgetCachedHeightmaps(this.viewerRenderer.renderer, heightMapsInUse);
    }

    // Dispose materials after this frame so that new materials may reuse shaders from the three.js shader cache.
    setTimeout(function () {
      for (const material of prevLoadedMaterials) {
        material.dispose();
      }
    }, 0);
    for (const disposable of this.disposables) {
      disposable.dispose();
    }
    this.disposables = [];

    let displayMaterialParams = this.activeMaterialParams;
    if (this.autoTrimVariant !== "Base") {
      displayMaterialParams = generateMaterialTrimVariant(displayMaterialParams, MasonryTrimKind[this.autoTrimVariant]);
    }

    displayMaterialParams = structuredClone(displayMaterialParams);

    const material = await designMaterialNew(
      displayMaterialParams,
      this.viewerRenderer.renderer,
      (url, _overrideFormat) => [this.fetchedTextures[url], new Promise((resolve, _reject) => setTimeout(resolve, 0))],
      this.viewer.hasSSFXRenderer,
      undefined,
      loadOptions,
    );

    if ((material.userData.mainMaskTexResolution ?? 0) > 0) {
      this.setState({
        mainMaskTexPixelWidth: displayMaterialParams.heightMapScaleU / material.userData.mainMaskTexResolution,
      });
    } else {
      this.setState({ mainMaskTexPixelWidth: -1 });
    }

    this.material = material;
    this.loadedMaterials.push(material);
    this.setActiveMaterialMetadataState();

    if (this.viewerRenderer.hasSSFXRenderer && this.viewer) {
      if (this.lightProbeGrid && this.siteGeneration !== this.lightProbeSiteGeneration) {
        this.lightProbeGrid.probesNeedUpdate = true;
        this.lightProbeSiteGeneration = this.siteGeneration;
        this.viewer.scene.staticContent.add(this.lightProbeGrid);
        this.viewer.scene.updateLightProbesList();
      }

      if (this.thumbnailImgRef.current) {
        const renderParams = ensureDefined(this.viewerRenderer.ssfxParamsManager).getRenderParams();
        renderParams.renderPipelineParams.output = SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS;
        const thumbnail = designMaterialRenderImageOptimized(
          material,
          this.viewerRenderer.services.imageRenderer,
          renderParams,
          {
            width: 256,
            height: 256,
            worldWidth: THUMBNAIL_WORLD_WIDTH,
          },
        );
        if (thumbnail) {
          this.thumbnailImgRef.current.src = thumbnail;
        }
      }
    }

    if (this.display === "material") {
      const mesh = this.getMaterialPreviewMesh(this.material);
      this.processMaterialGeometry(mesh);
      this.displayContent(mesh, true);
    } else if (this.display === "heightmap" || this.display === "bakedNormalMap") {
      const preprocessedMaterialParams = preprocessMaterialParams(this.activeMaterialParams);
      const heightMap = await heightMapFromDesignMaterial(
        this.viewerRenderer.renderer,
        preprocessedMaterialParams,
        false,
        false,
      );
      const mapGeo = new DoubleSidedPlaneGeometry(120, 120);
      const mapDisplayMaterial = new ShaderMaterial({
        vertexShader: UVVertexShader,
        fragmentShader: ChannelVisualizationFragmentShader,
        uniforms: {
          map: { value: heightMap ? heightMap.texture : null },
          channels: { value: [0, 0, 0, -1] },
        },
      });
      if (this.display === "bakedNormalMap") {
        mapDisplayMaterial.uniforms.channels.value = [0, 1, 2, -1];
      }
      const heightMapDisplay = new Mesh(mapGeo, mapDisplayMaterial);
      heightMapDisplay.rotation.set(Math.PI / 2, Math.PI, 0);
      this.displayContent(heightMapDisplay, false);
      this.setState({ heightMapResolution: heightMap ? heightMap.pixelSize : 0 });
    } else if (this.display === "heightmapScene" || this.display === "heightmapSceneWireframe") {
      const preprocessedMaterialParams = preprocessMaterialParams(this.activeMaterialParams);
      const { parent, camera, geometries } = renderHeightMapRenderer(preprocessedMaterialParams);
      const heightMapDisplay = parent;
      if (this.display === "heightmapSceneWireframe") {
        heightMapDisplay.traverse((o) => {
          if (o instanceof Mesh) {
            const materialCopy = ((o as Mesh).material as ShaderMaterial).clone();
            materialCopy.wireframe = true;
            o.material = materialCopy;
          }
        });
      }
      if (camera !== null) {
        const cameraHelper = new CameraHelper(camera);
        heightMapDisplay.add(cameraHelper);
        this.disposables.push(cameraHelper);
      }
      this.disposables.push(...geometries);
      this.displayContent(heightMapDisplay, false);
    } else if (
      this.display === "geometryConversion" ||
      this.display === "geometryConversionWireframe" ||
      this.display === "exportGeometryConversion" ||
      this.display === "exportGeometryConversionWireframe"
    ) {
      const geoMaterial = await designMaterialNew(
        this.activeMaterialParams,
        this.viewerRenderer.renderer,
        (url, _overrideFormat) => [
          this.fetchedTextures[url],
          new Promise((resolve, _reject) => setTimeout(resolve, 0)),
        ],
        this.viewer.hasSSFXRenderer,
        undefined,
        {
          ...defaultDesignMaterialLoadOptions,
          disableSDF: true,
          disableLayers: true,
        },
      );
      this.loadedMaterials.push(geoMaterial);

      if (this.display === "exportGeometryConversion" || this.display === "exportGeometryConversionWireframe") {
        const obj = new Object3D();
        const halfSize = 40;
        const baseFace = faceNew(
          [polyLine2MakeAxisAlignedRect(-halfSize, -halfSize, halfSize, halfSize)],
          planeNewXY(),
        );
        const preprocessedMaterialParams = preprocessMaterialParams(this.activeMaterialParams);
        const generatedGeos = generateThreeGeometryWithinFaces(
          [baseFace],
          "ADJUST_ALL",
          preprocessedMaterialParams.sdf,
          preprocessedMaterialParams.transparency,
          UVGeneration.NONE,
        );
        for (const generatedGeo of generatedGeos) {
          generatedGeo.computeVertexNormals();
          const geoMesh = new Mesh(generatedGeo, geoMaterial);
          obj.add(geoMesh);
          if (this.display === "exportGeometryConversionWireframe") {
            const wireframeGeo = new WireframeGeometry(generatedGeo);
            const line = new LineSegments(wireframeGeo);
            obj.add(line);
          }
        }
        this.processMaterialGeometry(obj);
        this.displayContent(obj, true);
      } else {
        const baseMesh = this.getMaterialPreviewMesh(geoMaterial);

        if (!(baseMesh instanceof Mesh) || baseMesh.geometry === undefined) {
          this.displayContent(undefined, true);
        } else {
          const layoutInfo = uvLayoutInfoNew();
          const preprocessedMaterialParams = preprocessMaterialParams(this.activeMaterialParams);
          const preprocessedSdf = preprocessedMaterialParams.sdf;
          const generatedGeo = generateThreeGeometryFromDesignMaterial(
            baseMesh.geometry,
            baseMesh.scale,
            "DONT_ADJUST",
            preprocessedSdf,
            getLayoutInfoForMaterial("editor", layoutInfo),
          );
          const sdfTransparent =
            preprocessedMaterialParams.transparency === "sdfTransparent" ||
            preprocessedMaterialParams.transparency === "metalScreen";
          const parent = sdfTransparent ? new Object3D() : baseMesh;
          if (generatedGeo) {
            generatedGeo.computeVertexNormals();
            const geoMesh = new Mesh(generatedGeo, geoMaterial);
            parent.add(geoMesh);
            if (this.display === "geometryConversionWireframe") {
              const wireframeGeo = new WireframeGeometry(generatedGeo);
              const line = new LineSegments(wireframeGeo);
              parent.add(line);
            }
          }
          this.processMaterialGeometry(parent);
          this.displayContent(parent, true);
        }
      }
    } else if (this.display === "libraryPreview" || this.display === "templatePreview") {
      const meshes = new Object3D();
      let x = 0;
      let y = 0;
      const previewCount = Math.min(
        this.previewedMaterialSet.length - this.previewStartIndex,
        this.currentPreviewLimit,
      );
      const columnCount = Math.ceil(Math.sqrt(previewCount));
      const spacing = 240;
      const offsetX = spacing * (columnCount - 1) * 0.5;
      const rowCount = Math.ceil(previewCount / columnCount);
      const offsetY = spacing * (rowCount - 1) * 0.5;
      for (let i = 0; i < previewCount; ++i) {
        const previewParams = this.previewedMaterialSet[i + this.previewStartIndex];
        const material = await designMaterialNew(
          previewParams.params,
          this.viewerRenderer.renderer,
          (url, _overrideFormat) => [
            this.fetchedTextures[url],
            new Promise((resolve, _reject) => setTimeout(resolve, 0)),
          ],
          this.viewer.hasSSFXRenderer,
          undefined,
          loadOptions,
        );
        this.loadedMaterials.push(material);
        const mesh = this.getMaterialPreviewMesh(material);
        this.processMaterialGeometry(mesh);
        mesh.position.set(x * spacing - offsetX, y * spacing - offsetY, 0);
        ++x;
        if (x >= columnCount) {
          ++y;
          x = 0;
        }
        mesh.userData.hoverName = previewParams.name;
        mesh.userData.materialParamsOnClick = previewParams.params;
        meshes.add(mesh);
      }
      this.displayContent(meshes, false);
    }

    if (this.updateSceneRequest > fulfilledUpdateRequest) {
      setTimeout(() => this.updateSceneAsync(), 0);
    } else {
      this.updateSceneInFlight = false;
    }
  }

  getLoadOptions(): DesignMaterialLoadOptions {
    let forceDetailRenderMode = undefined;
    if (this.forceDetailRenderMode !== null) {
      forceDetailRenderMode = DetailRenderMode[this.forceDetailRenderMode];
    }

    return {
      disableSDF: false,
      disableLayers: false,

      enableLightOffsetMap: true,
      offsetMapLightCount: 8,
      repeatingSDF: this.repeatingSDF,
      bakeColorTexture: this.bakeColorTexture,
      forceDetailRenderMode,
      layerViewMode: LayerViewMode[this.layerViewMode],
      componentViewMode: ComponentViewMode[this.componentViewMode],
      useWebgl2TextureAtlas: this.useWebgl2TextureAtlas,
      reproduceImplicitLodIntelBug: false,
    };
  }

  private previousPreview() {
    if (this.previewStartIndex > 0) {
      this.previewStartIndex = Math.max(0, this.previewStartIndex - this.currentPreviewLimit);
      this.updateScene();
    }
  }

  private nextPreview() {
    if (this.previewStartIndex + this.currentPreviewLimit < this.previewedMaterialSet.length) {
      this.previewStartIndex = Math.min(
        this.previewedMaterialSet.length - 1,
        this.previewStartIndex + this.currentPreviewLimit,
      );
      this.updateScene();
    }
  }

  resetTransform() {
    assertIsNotNull(this.viewer);
    this.geometryContainer.rotation.set(0, 0, 0);
    this.geometryContainer.updateMatrix();
    this.geometryContainer.updateMatrixWorld();
    this.viewer.scene.markShadowMapsForUpdate();
    this.viewer.scene.markRenderingNeedsUpdate();
  }

  private refreshCameraControls() {
    assertIsNotNull(this.viewer);
    let cameraControls: CanvasControls;
    if (this.orthographicCamera) {
      cameraControls = new OrthographicControls(this.viewer.camera, this.viewer.container, 0, 0, 0);
      cameraControls.enableRotate = true;
    } else {
      cameraControls = new PerspectiveControls(this.viewer.camera, this.viewer.container, 0, 0, 0);
    }
    cameraControls.addEventListener("change", () => this.transformControls.updateMatrixWorld());
    this.viewer.setControls(cameraControls);
    this.transformControls.camera = this.viewer.camera;
  }

  cycleCamera() {
    assertIsNotNull(this.viewer);
    const cameraIndex = (this.state.cameraIndex + 1) % perspectiveCameraSettings.length;
    this.setState({ cameraIndex });
    this.viewer.setCameraSettings(perspectiveCameraSettings[cameraIndex]);
    this.refreshCameraControls();
  }

  private async updateLayerConfigParams(layerConfig: LayerConfigParams) {
    applyLayerConfig(this.activeMaterialParams, layerConfig);
    void this.updateSceneAndGUI();
  }

  private async updateMaterialParams(
    materialParams: DesignMaterialParams,
    templateParams: DesignMaterialParams | undefined,
    undoBehavior: UndoBehavior,
  ) {
    if (materialParams) {
      sdfPatternsPopulateToMax(materialParams);
    }
    if (templateParams) {
      sdfPatternsPopulateToMax(templateParams);
    }
    if (undoBehavior === UndoBehavior.CLEAR_STACK) {
      this.materialParamsUndoStack = [structuredClone(materialParams)];
    } else if (undoBehavior === UndoBehavior.UNDOABLE) {
      this.materialParamsUndoStack.push(structuredClone(materialParams));
    }
    this.activeMaterialParams = materialParams;
    this.lastLoadedTemplateParams = templateParams;
    void this.updateSceneAndGUI();
  }

  private async updateSceneAndGUI() {
    if (this.disposeGUI) {
      this.disposeGUI();
    }

    const updateAndSaveParameters = (addToUndoStack = true) => {
      if (
        addToUndoStack &&
        (this.materialParamsUndoStack.length === 0 ||
          !designMaterialParamsEqual(
            this.activeMaterialParams,
            this.materialParamsUndoStack[this.materialParamsUndoStack.length - 1],
            true,
          ))
      ) {
        this.materialParamsUndoStack.push(structuredClone(this.activeMaterialParams));
      }
      this.updateScene();
      this.saveParamsToLocalStorage();
    };

    updateAndSaveParameters();

    const disposeCalls: (() => void)[] = [];
    let disposed = false;
    this.disposeGUI = () => {
      if (disposed) {
        return;
      }
      disposed = true;
      disposeCalls.forEach((f) => f());
    };

    const updateDisplayAndSaveParameters = (value) => {
      if (value === "templatePreview") {
        void this.previewAllTemplates();
        return;
      }
      updateAndSaveParameters();
    };

    assertIsNotNull(this.viewer);
    const viewerRenderer = this.viewer.viewerRenderer;

    assertIsNotUndefined(viewerRenderer.gui, "dat.gui is not initialized");
    const gui: dat.GUI = viewerRenderer.gui;

    const displayMode = gui
      .add(this, "display" as keyof DesignMaterialEditor, DisplayTypes)
      .onChange(updateDisplayAndSaveParameters)
      .listen();
    disposeCalls.push(() => gui.remove(displayMode));

    const trimVariant = gui
      .add(this, "autoTrimVariant" as keyof DesignMaterialEditor, Object.values(MasonryTrimKind))
      .onChange(updateAndSaveParameters)
      .listen();
    disposeCalls.push(() => gui.remove(trimVariant));

    const goemetryTypeOptions: { [key: string]: string } = {};
    goemetryTypeOptions[`box (${BOX_GEOMETRY_SIZE} inches wide)`] = "box";
    goemetryTypeOptions[`sphere (${SPHERE_GEOMETRY_RADIUS} inch radius)`] = "sphere";
    goemetryTypeOptions[`plane (${BOX_GEOMETRY_SIZE} inches wide)`] = "plane";
    goemetryTypeOptions[`large plane (${LARGE_PLANE_GEOMETRY_SIZE} inches wide)`] = "largePlane";
    goemetryTypeOptions["large slants"] = "largeSlants";
    goemetryTypeOptions["custom"] = "custom";
    const geometryType = gui
      .add(this, "geometryType" as keyof DesignMaterialEditor, goemetryTypeOptions)
      .onChange(updateAndSaveParameters)
      .listen();
    disposeCalls.push(() => gui.remove(geometryType));

    this.lightingType; // prevent this field from being marked as unused
    const lighting = gui
      .add(this, "lightingType" as keyof DesignMaterialEditor, [
        "morning",
        "morning HDR",
        "midday",
        "evening",
        "whitebox",
        "simple",
      ])
      .onChange((value) => {
        let envPreset: EnvPreset | null = null;
        if (value === "morning") {
          envPreset = EnvPreset.MORNING;
        } else if (value === "morning HDR") {
          envPreset = EnvPreset.MORNING_HDR;
        } else if (value === "midday") {
          envPreset = EnvPreset.MIDDAY;
        } else if (value === "evening") {
          envPreset = EnvPreset.EVENING;
        } else if (value === "whitebox") {
          envPreset = EnvPreset.WHITEBOX;
        } else if (value === "simple") {
          envPreset = null;
        }
        if (envPreset !== null && envPreset !== this.envPreset) {
          this.envPreset = envPreset;
          this.sceneLights = { ...envPresetSceneLights[this.envPreset] };
          ++this.siteGeneration;
          updateAndSaveParameters();
        } else {
          this.sceneLights = testLights;
        }
      });
    disposeCalls.push(() => gui.remove(lighting));

    const ground = gui.add(this, "groundEnabled" as keyof DesignMaterialEditor).onChange(() => {
      if (this.groundMesh && this.groundMesh.visible !== this.groundEnabled) {
        this.groundMesh.visible = this.groundEnabled;
        ++this.siteGeneration;
        updateAndSaveParameters();
      }
    });
    disposeCalls.push(() => gui.remove(ground));

    const transformReset = gui.add(this, "resetTransform");
    disposeCalls.push(() => gui.remove(transformReset));
    const cameraCycle = gui.add(this, "cycleCamera");
    disposeCalls.push(() => gui.remove(cameraCycle));

    const orthographicCamera = gui
      .add(this, "orthographicCamera" as keyof DesignMaterialEditor)
      .onChange(updateAndSaveParameters);
    disposeCalls.push(() => gui.remove(orthographicCamera));

    const updateLoadOptions = async () => {
      assertIsNotNull(this.material);
      assertIsNotNull(this.viewer);
      const fullyLoadedPromise: Promise<void> = setDesignMaterialOptions(
        this.material,
        this.viewerRenderer.renderer,
        this.viewer.hasSSFXRenderer,
        (url) => [this.fetchedTextures[url], new Promise((resolve, _reject) => setTimeout(resolve, 0))],
        undefined,
        this.getLoadOptions(),
      )[1];
      await fullyLoadedPromise;
      this.setActiveMaterialMetadataState();
      this.viewer.scene.markRenderingNeedsUpdate();
    };

    const folderLoadOptions = gui.addFolder("Material load options");
    disposeCalls.push(() => gui.removeFolder(folderLoadOptions));

    folderLoadOptions.add(this, "repeatingSDF" as keyof DesignMaterialEditor).onChange(updateLoadOptions);

    // Intentionally leaving out BakeColorTextureMode.FETCH_FROM_AWS since it doesn't make sense in the editor context.
    folderLoadOptions
      .add(this, "bakeColorTexture" as keyof DesignMaterialEditor, [
        BakeColorTextureMode.DONT_BAKE,
        BakeColorTextureMode.REPEATING_LAYERS,
        BakeColorTextureMode.BAKE,
        BakeColorTextureMode.FLAT_COLOR,
      ])
      .onChange(() => this.updateScene());
    folderLoadOptions
      .add(this, "forceDetailRenderMode" as keyof DesignMaterialEditor, [null, "SDF", "ParallaxMap"])
      .onChange(updateLoadOptions);
    folderLoadOptions
      .add(this, "layerViewMode" as keyof DesignMaterialEditor, enumKeys(LayerViewMode))
      .onChange(updateLoadOptions);
    folderLoadOptions
      .add(this, "componentViewMode" as keyof DesignMaterialEditor, enumKeys(ComponentViewMode))
      .onChange(updateLoadOptions);
    folderLoadOptions.add(this, "useWebgl2TextureAtlas" as keyof DesignMaterialEditor).onChange(updateLoadOptions);

    initMaterialEditorGUI(gui, this.activeMaterialParams, disposeCalls, updateAndSaveParameters);
  }

  setActiveMaterialMetadataState() {
    assertIsNotNull(this.material);
    let heightMapResolution = -1;
    let detailRenderModeNotice = "";
    if (
      this.material.userData.detailRenderMode === DetailRenderMode.ParallaxMap ||
      this.activeMaterialParams.heightMapName
    ) {
      heightMapResolution = this.material.userData.heightMapResolution;
    } else {
      const sdfNonSIMDMode =
        this.activeMaterialParams.sdf.common.randomTilesMode === "None" &&
        !sdfCompileTimeSettingsMatch(preprocessMaterialParams(this.activeMaterialParams).sdf.patterns);
      if (sdfNonSIMDMode) {
        detailRenderModeNotice = "The slow non-SIMD mode is active because SDF pattern settings don't match!";
      }
    }
    if (this.material.userData.detailRenderMode === DetailRenderMode.ParallaxMap) {
      if (DEFAULT_DETAIL_RENDER_MODE !== DetailRenderMode.ParallaxMap) {
        detailRenderModeNotice = "Parallax map mode is used, because: " + this.material.userData.detailRenderModeReason;
      }
      const preprocessed = preprocessMaterialParams(this.activeMaterialParams);
      const { normalizedPatterns } = normalizeSdfPatternRotation(preprocessed.sdf.common, preprocessed.sdf.patterns);
      if (
        sdfPeriod(preprocessed.sdf.common, normalizedPatterns, SdfPeriodPurpose.HeightMapPeriod).type !==
        PeriodType.VALID
      ) {
        detailRenderModeNotice = "Could not determine SDF repeat period. Generated parallax map might contain seams.";
      }
    }
    this.setState({ heightMapResolution, detailRenderModeNotice });
  }
}

function DesignMaterialEditorPage() {
  return <DesignMaterialEditor />;
}

export const Route = createFileRoute("/debug/design-material-editor")({
  component: withAuthentication(DesignMaterialEditorPage, { superAdmin: true }),
});
